import { useState, useRef, useLayoutEffect, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Stack,
  Typography,
  Card,
  Button,
  TextField,
  Tooltip,
  Snackbar,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { CHANGE_PASSWORD_ERROR_MESSAGE } from "../../utils/errorMessages";
import TuneIcon from "@mui/icons-material/Tune";
import LockKeyholeIcon from "@mui/icons-material/EnhancedEncryption";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import LogoutIcon from "@mui/icons-material/Logout";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { useNavigate, useLocation } from "react-router-dom";
import { useThemeMode } from "../../ThemeModeContext";
import { useAuth } from "../../auth/AuthProvider";
import CONFIG from "../../config/config";
import { apiFetch } from "../../services/http";
import { tokens } from "../../theme";
import { PageHead, pageContainerSx } from "../../components/ui";
import Profile from "../Profile/Profile";
import Payment from "../Payment/Payment";
import Privacy from "../Privacy/Privacy";
import { useNotificationPrefs } from "../../hooks/useNotificationPrefs";

// Account/Payment/Privacy reuse their standalone page components; their own routes
// still exist for other entry points (Header menu, Command Palette, Dashboard).
// Security is deliberately not a tab — its controls sit inside Account.
const NAV_ITEMS = [
  { key: "account", label: "Account", icon: PersonOutlineIcon },
  { key: "general", label: "General", icon: TuneIcon },
  { key: "payment", label: "Payment", icon: CreditCardIcon },
  { key: "privacy", label: "Privacy Policy", icon: LockKeyholeIcon },
];

// Per-section help shown by the info icon on the right of the tab bar. Changes
// with the active tab so the hint is always relevant to what's on screen.
const SECTION_HINTS = {
  account: "Manage your account: profile details, contact info and security.",
  general: "App preferences — theme, timezone and notifications.",
  payment: "Your plan, payment method and invoices. Billing is demo data for now.",
  privacy: "How Botriq collects, uses and protects your data.",
};

const TIMEZONES = [
  "(GMT+05:30) India Standard Time",
  "(GMT+00:00) UTC",
  "(GMT-08:00) Pacific Time",
  "(GMT+04:00) Gulf Standard Time",
];

const SectionCard = ({ title, children }) => (
  <Card sx={{ mb: 1.75, overflow: "hidden" }}>
    <Box sx={{ p: "13px 16px", borderBottom: `1px solid ${tokens.border}` }}>
      <Typography sx={{ fontSize: 13, fontWeight: 600, color: tokens.text }}>{title}</Typography>
    </Box>
    {children}
  </Card>
);

// Label on the left, control on the right — until the two stop fitting side by
// side, at which point the control drops onto its own line rather than being
// crushed against the label's descriptive sub-text.
const SettingRow = ({ label, sub, children, last }) => (
  <Stack
    direction={{ xs: "column", sm: "row" }}
    alignItems={{ xs: "stretch", sm: "center" }}
    gap={1.5}
    sx={{
      px: 2,
      py: 1.6,
      borderBottom: last ? "none" : `1px solid ${tokens.border}`,
    }}
  >
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography sx={{ fontSize: 13, fontWeight: 600, color: tokens.text }}>{label}</Typography>
      {sub && <Typography sx={{ fontSize: 11.5, color: tokens.text3, mt: 0.25 }}>{sub}</Typography>}
    </Box>
    {children}
  </Stack>
);

// Module scope (not inside Settings) keeps this component's identity stable;
// nesting it would remount the TextField on every keystroke and drop focus.
// `reveal={false}` omits the show/hide eye, used for the current password.
const PwdField = ({
  field,
  label,
  showKey,
  reveal = true,
  form,
  errors,
  showPwds,
  onChange,
  onToggleShow,
}) => (
  <Box sx={{ position: "relative" }}>
    <TextField
      // `placeholder` (not the floating outlined label) so the hint sits inside
      // the box and simply disappears as you type — it can never overlap/cut the
      // border the way a notched label does.
      size="small"
      placeholder={label}
      type={reveal && showPwds[showKey] ? "text" : "password"}
      value={form[field]}
      onChange={onChange(field)}
      fullWidth
      error={!!errors[field]}
      helperText={errors[field]}
      sx={{
        // Fixed field height with the input text vertically centered.
        "& .MuiOutlinedInput-root": {
          height: 44,
          borderRadius: "9px",
          fontSize: 13,
          background: tokens.surface2,
        },
        "& .MuiOutlinedInput-input": {
          height: 44,
          boxSizing: "border-box",
          display: "flex",
          alignItems: "center",
          pr: reveal ? 5 : 1.5,
        },
      }}
    />
    {reveal && (
      // Vertically centered against the 44px field (helperText, if any, sits below).
      <button
        type="button"
        onClick={() => onToggleShow(showKey)}
        style={{
          position: "absolute",
          right: 10,
          top: 22,
          transform: "translateY(-50%)",
          background: "none",
          border: "none",
          color: tokens.text3,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
        }}
      >
        {showPwds[showKey] ? <FaEyeSlash /> : <FaEye />}
      </button>
    )}
  </Box>
);

const Toggle = ({ on, onToggle, disabled = false }) => (
  <Box
    component="button"
    onClick={disabled ? undefined : onToggle}
    disabled={disabled}
    sx={{
      width: 38,
      height: 22,
      borderRadius: "12px",
      border: "none",
      position: "relative",
      flexShrink: 0,
      cursor: disabled ? "default" : "pointer",
      opacity: disabled ? 0.45 : 1,
      background: on ? tokens.accent : tokens.surface3,
      transition: "background .13s",
    }}
  >
    <Box
      sx={{
        position: "absolute",
        top: 2,
        left: on ? 18 : 2,
        width: 18,
        height: 18,
        borderRadius: "50%",
        background: "#fff",
        transition: "left .13s",
      }}
    />
  </Box>
);

const Settings = () => {
  const { mode, toggleMode } = useThemeMode();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [pwdForm, setPwdForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [showPwds, setShowPwds] = useState({
    current: false,
    newPwd: false,
    confirm: false,
  });
  const [pwdErrors, setPwdErrors] = useState({});
  const [savingPwd, setSavingPwd] = useState(false);
  const [snack, setSnack] = useState({
    open: false,
    msg: "",
    severity: "success",
  });
  const [logoutAllOpen, setLogoutAllOpen] = useState(false);
  const [pwdDialogOpen, setPwdDialogOpen] = useState(false);
  // Persist the active tab so it survives the theme-provider's intentional
  // whole-tree remount on light/dark toggle (which would otherwise reset this
  // to the default and bounce the user back to Account).
  const [section, setSection] = useState(() => {
    try {
      return sessionStorage.getItem("botriq:settingsTab") || "account";
    } catch {
      return "account";
    }
  });
  // Switch tab and persist the choice, so it survives the remount described above.
  const selectSection = (next) => {
    setSection(next);
    try {
      sessionStorage.setItem("botriq:settingsTab", next);
    } catch {
      /* ignore */
    }
  };
  // Open a specific tab when navigated here with `state.tab` (e.g. the header
  // profile menu's Account / Payment items). Works even when already on
  // Settings, since it reacts to each navigation.
  useEffect(() => {
    const t = location.state?.tab;
    if (t && NAV_ITEMS.some((n) => n.key === t)) selectSection(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key, location.state]);
  // General preferences.
  const [timezone, setTimezone] = useState(TIMEZONES[0]);
  // Notification categories — synced with UserV1.notificationPrefs, the same
  // field the mobile app's Settings screen and the backend's push gating
  // read (see botriq-user-mobile-app/src/store/notificationPrefsStore.js).
  const { prefs: notifPrefs, setPref: setNotifPref } = useNotificationPrefs();

  const handlePwdChange = (field) => (e) => {
    setPwdForm((p) => ({ ...p, [field]: e.target.value }));
    setPwdErrors((p) => ({ ...p, [field]: "" }));
  };

  const validatePwd = () => {
    const e = {};
    if (!pwdForm.currentPassword) e.currentPassword = "Current password required";
    if (!pwdForm.newPassword) e.newPassword = "New password required";
    else if (pwdForm.newPassword.length < 8) e.newPassword = "Minimum 8 characters";
    if (pwdForm.newPassword !== pwdForm.confirmPassword)
      e.confirmPassword = "Passwords do not match";
    setPwdErrors(e);
    return !Object.keys(e).length;
  };

  const handleChangePwd = async () => {
    if (!validatePwd()) return;
    setSavingPwd(true);
    try {
      const token = localStorage.getItem("portalToken");
      const res = await apiFetch(`${CONFIG.BASE_URL}/api/portal/profile/change-password`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          currentPassword: pwdForm.currentPassword,
          newPassword: pwdForm.newPassword,
        }),
      });
      if (!res.ok) {
        const d = await res.json();
        throw new Error(d.message);
      }
      setPwdForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
      setPwdDialogOpen(false);
      setSnack({
        open: true,
        msg: "Password changed successfully",
        severity: "success",
      });
    } catch {
      setSnack({
        open: true,
        msg: CHANGE_PASSWORD_ERROR_MESSAGE,
        severity: "error",
      });
    } finally {
      setSavingPwd(false);
    }
  };

  // Open the change-password dialog with a clean form (no stale values/errors).
  const openPwdDialog = () => {
    setPwdForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
    setPwdErrors({});
    setShowPwds({ current: false, newPwd: false, confirm: false });
    setPwdDialogOpen(true);
  };

  const toggleShow = (key) => setShowPwds((p) => ({ ...p, [key]: !p[key] }));

  // End the session and return to login (mirrors Profile's logout flow).
  const handleLogoutAll = async () => {
    await logout();
    setLogoutAllOpen(false);
    navigate("/login", { replace: true });
  };

  const selectStyle = {
    // Caps at the original 260px on a roomy row but shrinks with the column
    // instead of overflowing it — a fixed 260px control plus the page gutter
    // does not fit inside a 320px viewport.
    width: "100%",
    maxWidth: 260,
    height: 36,
    padding: "0 10px",
    borderRadius: 9,
    fontSize: 13,
    fontFamily: "inherit",
    border: `1px solid ${tokens.border}`,
    background: tokens.surface2,
    color: tokens.text,
    cursor: "pointer",
  };

  // Animated active-tab underline: measure the active tab button and slide the
  // ink bar to it. Re-measures on section change and on window resize.
  const tabRefs = useRef({});
  const [ink, setInk] = useState({ left: 0, width: 0 });
  useLayoutEffect(() => {
    const el = tabRefs.current[section];
    if (el) setInk({ left: el.offsetLeft, width: el.offsetWidth });
    const onResize = () => {
      const e = tabRefs.current[section];
      if (e) setInk({ left: e.offsetLeft, width: e.offsetWidth });
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [section]);

  // In-page heading (matches every other page, e.g. Notifications). The
  // redundant header-bar title is removed globally in Header.jsx.
  return (
    <Box sx={{ ...pageContainerSx(), pt: 0 }}>
      {/* Heading + tab bar pinned together as ONE sticky block, so they move as
          a unit on scroll — no offset guessing, no overlap clipping the help
          icon. Bottom margin sits outside the sticky block. An opaque
          background is required here: without one, scrolled-under tab content
          (e.g. "Company details") shows through and visually overlaps the
          heading/tabs instead of sitting behind them. */}
      <Box
        sx={{
          position: { sm: "sticky" },
          top: 0,
          zIndex: 2,
          pt: { xs: 1.25, sm: 1.5 },
          mb: 2.5,
          background: tokens.surface,
        }}
      >
        <PageHead
          title="Settings"
          sub="Manage your account preferences"
          icon="settings"
          sx={{ mb: 0, pb: 1.25 }}
        />

        {/* Horizontal tab bar (Account · General · Payment · Privacy) with an
            animated underline that slides to the active tab. */}
        <Box
          role="tablist"
          sx={{
            position: "relative",
            display: "flex",
            gap: 0.25,
            borderBottom: `1px solid ${tokens.border}`,
            overflowX: "auto",
          }}
        >
          {NAV_ITEMS.map(({ key, label, icon: Icon }) => {
            const active = section === key;
            return (
              <Box
                key={key}
                role="tab"
                aria-selected={active}
                component="button"
                ref={(el) => {
                  tabRefs.current[key] = el;
                }}
                onClick={() => selectSection(key)}
                sx={{
                  position: "relative",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 1,
                  padding: "11px 16px 13px",
                  border: "none",
                  background: "none",
                  cursor: "pointer",
                  fontFamily: "inherit",
                  fontSize: 13.5,
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                  color: active ? tokens.accent : tokens.text2,
                  transition: "color .15s ease",
                  "&:hover": active ? {} : { color: tokens.text },
                }}
              >
                <Icon sx={{ fontSize: 17 }} />
                {label}
              </Box>
            );
          })}

          {/* Per-section help — a round "?" button pinned to the right of the tab
            bar. Hovering reveals a hint about the active section (text changes
            with the tab). Fills the right-hand space cleanly. */}
          <Tooltip title={SECTION_HINTS[section] || ""} arrow placement="bottom-end">
            <Box
              component="button"
              type="button"
              aria-label={`Help — about ${NAV_ITEMS.find((n) => n.key === section)?.label || "this section"}`}
              sx={{
                ml: "auto",
                alignSelf: "center",
                mb: "4px",
                display: "grid",
                placeItems: "center",
                width: 30,
                height: 30,
                // No outer ring/fill — the HelpOutline icon already draws its own
                // circle, so wrapping it in a bordered button showed two circles.
                border: "none",
                background: "transparent",
                color: tokens.text3,
                cursor: "help",
                transition: "color .15s ease",
                "&:hover": { color: tokens.accent },
              }}
            >
              <HelpOutlineIcon sx={{ fontSize: 19 }} />
            </Box>
          </Tooltip>

          {/* sliding active underline */}
          <Box
            sx={{
              position: "absolute",
              bottom: 0,
              height: "2px",
              background: tokens.accent,
              borderRadius: "2px 2px 0 0",
              left: `${ink.left}px`,
              width: `${ink.width}px`,
              transition: "left .22s cubic-bezier(.4,0,.2,1), width .22s cubic-bezier(.4,0,.2,1)",
            }}
          />
        </Box>
      </Box>

      {/* Tab content — switches by section (keyed so it fades on change). */}
      <Box key={section} sx={{ animation: "bq-page-in .2s ease", mt: 0 }}>
        {section === "account" && (
          <Profile embedded onNavigateSection={selectSection} onChangePassword={openPwdDialog} />
        )}

        {section === "payment" && <Payment embedded />}

        {section === "general" && (
          <SectionCard title="General preferences">
            {/* Theme */}
            <SettingRow label="Theme" sub="Light or dark interface">
              <Stack direction="row" gap={0.75}>
                {["light", "dark"].map((t) => {
                  const active = mode === t;
                  return (
                    <Box
                      key={t}
                      component="button"
                      onClick={() => {
                        if (mode !== t) toggleMode();
                      }}
                      sx={{
                        padding: "6px 14px",
                        fontSize: 12.5,
                        fontWeight: 600,
                        borderRadius: "8px",
                        textTransform: "capitalize",
                        border: `1px solid ${active ? tokens.accent : tokens.border}`,
                        background: active ? tokens.accentSoft : tokens.surface2,
                        color: active ? tokens.accent : tokens.text2,
                        cursor: "pointer",
                        fontFamily: "inherit",
                      }}
                    >
                      {t}
                    </Box>
                  );
                })}
              </Stack>
            </SettingRow>

            {/* Timezone */}
            <SettingRow label="Timezone" sub="Used for dates and activity times">
              <Box
                component="select"
                value={timezone}
                onChange={(e) => setTimezone(e.target.value)}
                sx={selectStyle}
              >
                {TIMEZONES.map((tz) => (
                  <option key={tz} value={tz}>
                    {tz}
                  </option>
                ))}
              </Box>
            </SettingRow>
          </SectionCard>
        )}

        {section === "general" && (
          <SectionCard title="Notification preferences">
            <SettingRow
              label="Push Notifications"
              sub="Master switch — turn off to silence every category below"
            >
              <Toggle
                on={notifPrefs.pushEnabled}
                onToggle={() => setNotifPref("pushEnabled", !notifPrefs.pushEnabled)}
              />
            </SettingRow>
            <SettingRow label="Robot Status" sub="Robot assignment changes and live sensor health">
              <Toggle
                on={notifPrefs.robotStatus}
                onToggle={() => setNotifPref("robotStatus", !notifPrefs.robotStatus)}
                disabled={!notifPrefs.pushEnabled}
              />
            </SettingRow>
            <SettingRow label="Advertisement Updates" sub="Approval and rejection notifications">
              <Toggle
                on={notifPrefs.advertisements}
                onToggle={() => setNotifPref("advertisements", !notifPrefs.advertisements)}
                disabled={!notifPrefs.pushEnabled}
              />
            </SettingRow>
            <SettingRow label="Delivery Updates" sub="Navigation and delivery notifications">
              <Toggle
                on={notifPrefs.deliveryUpdates}
                onToggle={() => setNotifPref("deliveryUpdates", !notifPrefs.deliveryUpdates)}
                disabled={!notifPrefs.pushEnabled}
              />
            </SettingRow>
            <SettingRow label="Account Alerts" sub="Security and account status alerts" last>
              <Toggle
                on={notifPrefs.accountAlerts}
                onToggle={() => setNotifPref("accountAlerts", !notifPrefs.accountAlerts)}
                disabled={!notifPrefs.pushEnabled}
              />
            </SettingRow>
          </SectionCard>
        )}

        {section === "privacy" && <Privacy embedded />}
      </Box>

      <Dialog open={pwdDialogOpen} onClose={() => setPwdDialogOpen(false)} maxWidth="xs" fullWidth>
        {/* <form> so Enter submits the password change (keyboard accessibility). */}
        <Box
          component="form"
          onSubmit={(e) => {
            e.preventDefault();
            handleChangePwd();
          }}
        >
          <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>Change password</DialogTitle>
          <DialogContent>
            <Stack gap={1.75} sx={{ pt: 1 }}>
              <PwdField
                field="currentPassword"
                label="Current Password"
                showKey="current"
                reveal={false}
                form={pwdForm}
                errors={pwdErrors}
                showPwds={showPwds}
                onChange={handlePwdChange}
                onToggleShow={toggleShow}
              />
              <PwdField
                field="newPassword"
                label="New Password"
                showKey="newPwd"
                form={pwdForm}
                errors={pwdErrors}
                showPwds={showPwds}
                onChange={handlePwdChange}
                onToggleShow={toggleShow}
              />
              <PwdField
                field="confirmPassword"
                label="Confirm New Password"
                showKey="confirm"
                reveal={false}
                form={pwdForm}
                errors={pwdErrors}
                showPwds={showPwds}
                onChange={handlePwdChange}
                onToggleShow={toggleShow}
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
            <Button
              onClick={() => setPwdDialogOpen(false)}
              variant="outlined"
              sx={{ borderColor: tokens.border, color: tokens.text }}
            >
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={savingPwd}>
              {savingPwd ? (
                <CircularProgress size={14} sx={{ color: "#fff" }} />
              ) : (
                "Update password"
              )}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog open={logoutAllOpen} onClose={() => setLogoutAllOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>Log out</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 13, color: tokens.text2 }}>
            This ends every active session for your account, including this one.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={() => setLogoutAllOpen(false)}
            variant="outlined"
            sx={{ borderColor: tokens.border, color: tokens.text }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleLogoutAll}
            variant="contained"
            color="error"
            startIcon={<LogoutIcon />}
          >
            Log out
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity={snack.severity} sx={{ borderRadius: "10px" }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

SectionCard.propTypes = {
  title: PropTypes.node,
  children: PropTypes.node,
};

SettingRow.propTypes = {
  label: PropTypes.node,
  sub: PropTypes.node,
  children: PropTypes.node,
  last: PropTypes.bool,
};

PwdField.propTypes = {
  field: PropTypes.string.isRequired,
  label: PropTypes.string,
  showKey: PropTypes.string.isRequired,
  reveal: PropTypes.bool,
  form: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired,
  showPwds: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  onToggleShow: PropTypes.func.isRequired,
};

Toggle.propTypes = {
  on: PropTypes.bool,
  onToggle: PropTypes.func,
  disabled: PropTypes.bool,
};

export default Settings;
